import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { fetchBoards } from '@/services/kanbanBoardService';
import { toast } from 'react-toastify';
import { FaExchangeAlt } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

// Emoji fallback for FA icon names in <option> elements (which can't render React components)
const iconToEmoji = {
    'fa-globe': '🌐', 'fa-globe-africa': '🌍', 'fa-building': '🏢', 'fa-briefcase': '💼',
    'fa-clipboard-list': '📋', 'fa-tasks': '✅', 'fa-cog': '⚙️', 'fa-cogs': '⚙️',
    'fa-users': '👥', 'fa-user': '👤', 'fa-star': '⭐', 'fa-heart': '❤️',
    'fa-bolt': '⚡', 'fa-fire': '🔥', 'fa-bug': '🐛', 'fa-laptop': '💻',
    'fa-server': '🖥️', 'fa-shield-alt': '🛡️', 'fa-lock': '🔒', 'fa-leaf': '🌿',
    'fa-chart-line': '📈', 'fa-home': '🏠', 'fa-truck': '🚚', 'fa-wrench': '🔧',
    'fa-tools': '🛠️', 'fa-book': '📖', 'fa-envelope': '✉️', 'fa-phone': '📞',
    'fa-calendar': '📅', 'fa-calendar-alt': '📅', 'fa-flag': '🚩', 'fa-bell': '🔔',
    'fa-cloud': '☁️', 'fa-database': '🗄️', 'fa-project-diagram': '📊',
};

/**
 * ActionItemBoardSelector Component
 * Allows moving an action item to a different Kanban board.
 *
 * Only boards where the current user has "Edit" or "Admin" permission are
 * shown as valid targets. View-only boards (e.g. public boards the user cannot
 * edit) are deliberately excluded to prevent a confusing false-selected state
 * when the server rejects the move with 403.
 *
 * @param {Object}   props
 * @param {number}   props.currentBoardId - Current board ID the action item is on
 * @param {Function} props.onBoardChange  - Async callback; should return true on success,
 *                                          false on failure so this component can revert
 * @param {boolean}  props.disabled       - Whether the selector is disabled
 */
const ActionItemBoardSelector = ({ currentBoardId, onBoardChange, disabled = false }) => {
    const { apiFetch } = useAuth();
    const [allBoards, setAllBoards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBoardId, setSelectedBoardId] = useState(currentBoardId);

    // Define loadBoards BEFORE the useEffect that calls it
    const loadBoards = useCallback(async () => {
        try {
            const boardsData = await fetchBoards(apiFetch);
            setAllBoards(boardsData);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to load boards:', error);
            toast.error('Failed to load boards');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        // Wrap in async IIFE to satisfy react-hooks/set-state-in-effect:
        // async calls that eventually call setState must not be invoked
        // synchronously in the effect body.
        (async () => {
            await loadBoards();
        })();
    }, [loadBoards]);

    useEffect(() => {
        // Defer the state update via setTimeout to satisfy react-hooks/set-state-in-effect.
        // This syncs the controlled select value whenever the parent's currentBoardId changes
        // (e.g. after a successful board move updates the action item).
        setTimeout(() => setSelectedBoardId(currentBoardId), 0);
    }, [currentBoardId]);

    // Only expose boards where the user has Edit or Admin rights as valid move targets.
    // View-only boards (public boards with no explicit grant, or explicit View grants)
    // must not appear here — selecting one would immediately be rejected by the server
    // with a 403 and leave the selector in a stale/false-selected state.
    const editableBoards = allBoards.filter(board =>
        board.isOwner ||
        board.permissionLevel === 'Admin' ||
        board.permissionLevel === 'Edit'
    );

    const handleBoardChange = async (e) => {
        const newBoardId = e.target.value === '' ? null : parseInt(e.target.value, 10);

        // Optimistically show the new selection so the UI feels responsive.
        // If the parent's save call fails (returns false or throws), we revert.
        const previousBoardId = selectedBoardId;
        setSelectedBoardId(newBoardId);

        try {
            const succeeded = await onBoardChange(newBoardId);
            // Explicit false return from parent signals a failed save — revert
            if (succeeded === false) {
                setSelectedBoardId(previousBoardId);
            }
        } catch (err) {
            // Defensive: if the callback itself throws, always revert the selection
            Sentry.captureException(err);
            setSelectedBoardId(previousBoardId);
        }
    };

    if (loading) {
        return <div className="board-selector-loading">Loading boards...</div>;
    }

    if (editableBoards.length === 0) {
        return (
            <div className="board-selector-empty">
                <small>No editable boards available. Create a board to organize action items.</small>
            </div>
        );
    }

    const currentBoard = allBoards.find(b => b.boardId === currentBoardId);

    return (
        <div className="action-item-board-selector">
            <label htmlFor="boardSelect">
                <FaExchangeAlt /> Kanban Board
            </label>
            <select
                id="boardSelect"
                value={selectedBoardId || ''}
                onChange={handleBoardChange}
                disabled={disabled}
                className="form-select"
            >
                <option value="">No Board (General Tasks)</option>
                {editableBoards.map(board => (
                    <option key={board.boardId} value={board.boardId}>
                        {board.icon ? `${iconToEmoji[board.icon] || ''} ` : ''}{board.name}
                        {board.boardId === currentBoardId ? ' (Current)' : ''}
                    </option>
                ))}
            </select>
            {currentBoard && (
                <small className="board-info">
                    Currently on: <strong>{currentBoard.name}</strong>
                </small>
            )}
        </div>
    );
};

export default ActionItemBoardSelector;
